import { Alert, Form, Input, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import { WarehouseOption, listWarehouses } from "../../logistics/logistics.api";
import { ReturnRow, changeReturnWarehouse } from "../returns.api";

interface Props {
  row: ReturnRow | null;
  onClose: () => void;
  onChanged: () => void;
}

const ChangeWarehouseModal: React.FC<Props> = ({ row, onClose, onChanged }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

  useEffect(() => {
    if (!row) return;
    form.resetFields();
    listWarehouses().then(setWarehouses);
  }, [row, form]);

  const handleOk = async () => {
    if (!row) return;
    const values = await form.validateFields();
    setSaving(true);
    const ok = await changeReturnWarehouse(row.stage_id, {
      warehouse_id: values.warehouse_id,
      reason: values.reason?.trim() || undefined,
    });
    setSaving(false);
    if (ok) {
      onChanged();
      onClose();
    }
  };

  return (
    <Modal
      title={`Change warehouse — return #R-${row?.stage_id ?? ""}`}
      open={row !== null}
      onCancel={onClose}
      onOk={handleOk}
      okText="Change warehouse"
      confirmLoading={saving}
      destroyOnClose
      centered
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="The pickup truck will be re-booked to the new warehouse."
          description="Other returns from this order on the same pickup move with it. Allowed only until a vehicle is assigned."
        />
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item label="Current warehouse">{row?.destination_label || "—"}</Form.Item>
          <Form.Item
            label="New warehouse"
            name="warehouse_id"
            rules={[{ required: true, message: "Pick a warehouse" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select warehouse"
              options={warehouses
                .filter((w) => w.id !== row?.destination_wh_id)
                .map((w) => ({ value: w.id, label: w.city ? `${w.label} · ${w.city}` : w.label }))}
            />
          </Form.Item>
          <Form.Item label="Reason (optional)" name="reason">
            <Input.TextArea rows={2} maxLength={500} placeholder="e.g. customer is closer to the other hub" />
          </Form.Item>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ChangeWarehouseModal;
